import React from 'react';
import IndustryPageTemplate from '../../components/IndustryPageTemplate';
import SEO from '../../components/SEO';

const MarketingAgency = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How can Local Business Heroes help my marketing agency business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Local Business Heroes helps marketing agency businesses capture more leads, automate follow-ups, reduce no-shows, and grow faster. Rated 4.9/5 by 1,200+ UK businesses."
        }
      },
      {
        "@type": "Question",
        "name": "How much does it cost for marketing agency businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Plans start at £297/month with a 14-day trial for just £5. Most marketing agency businesses see ROI within the first month."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need technical skills to use it?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Local Business Heroes is designed for busy business owners with zero tech experience. Setup takes 5 minutes with UK-based support."
        }
      },
      {
        "@type": "Question",
        "name": "Will it work for my marketing agency business?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Local Business Heroes is specifically designed for local service businesses like marketing agency. Features include appointment scheduling, automated reminders, lead capture, and customer management."
        }
      }
    ]
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CRM for Marketing Agency - Local Business Heroes",
    "applicationCategory": "BusinessApplication",
    "offers": {
      "@type": "Offer",
      "price": "297",
      "priceCurrency": "GBP"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "1200"
    }
  };

  const breadcrumbs = [
    { name: "Home", url: "/" },
    { name: "Industries", url: "/industries" },
    { name: "Marketing Agency CRM", url: "/industries/marketing-agency" }
  ];

  const industryData = {
    name: 'Marketing Agency',
    icon: '📊',
    title: 'Stop Chasing Leads & Start Closing High-Ticket Marketing Retainers',
    subtitle: 'Automate discovery calls, showcase case studies, and convert project inquiries into recurring monthly retainers',
    
    // Problem Section - Challenges they face
    challenges: [
      'Spending hours on discovery calls with unqualified leads who can\'t afford your services',
      'Losing projects to agencies who respond to RFPs faster with professional proposals',
      'One-off project work only - no pipeline of recurring retainer clients',
      'Prospects ghost you after receiving proposals with no explanation',
      'Can\'t effectively showcase results and case studies to demonstrate ROI',
      'Manual client onboarding takes days and delays project kickoff',
      'Existing clients don\'t renew retainers because there\'s no proactive engagement',
      'Referrals happen occasionally but there\'s no systematic referral program'
    ],
    
    // Solution Section - How we solve these
    solutions: [
      'Automated discovery questionnaire pre-qualifies leads by budget, goals, and timeline before wasting time on calls',
      'Interactive ROI calculator demonstrates potential results (leads, sales, ROI) based on their industry and ad spend',
      'Proposal automation generates professional marketing proposals in minutes with custom case studies and pricing packages',
      'Follow-up sequences automatically re-engage prospects who received proposals but haven\'t decided yet',
      'Client success portal provides monthly reports, campaign analytics, and performance dashboards to justify retainer value',
      'Renewal automation starts conversations 60 days before retainer end dates with performance summaries and upsell opportunities',
      'Referral program automation incentivizes existing clients to refer other businesses with trackable referral links'
    ],
    
    // Features Section - What's included in snapshot
    features: [
      'Automated Discovery Questionnaire (budget, goals, timeline pre-qualification)',
      'Marketing ROI Calculator & Lead Generation Estimator',
      'Case Study Showcase Library (by industry & service type)',
      'Proposal Generator with Custom Pricing Packages',
      'Proposal Follow-Up Sequences (for non-converted leads)',
      'Client Onboarding Automation (contracts, questionnaires, kickoff scheduling)',
      'Client Success Portal (monthly reports & campaign dashboards)',
      'Retainer Renewal Reminder Automation (60 & 30 day alerts)',
      'Referral Program Management & Tracking',
      'Service Package Builder (SEO, PPC, Social, Email, Content)',
      'Automated Meeting Scheduler (discovery calls, strategy sessions)',
      'Google Review & Testimonial Request Automation',
      'White-Label Client Reporting Dashboard',
      'CRM Pipeline Management (leads → proposals → clients)'
    ],
    
    // Results Section - Success metrics
    results: [
      '68% proposal-to-retainer conversion improvement',
      '£12.4K average monthly recurring retainer revenue added',
      '4.1x more qualified discovery calls booked'
    ],
    
    // SEO Section
    metaTitle: 'Marketing Agency Growth Platform | Close More High-Ticket Retainers',
    metaDescription: 'Stop chasing unqualified leads. Automated discovery, ROI calculators, proposal generation, and retainer renewal automation for marketing agencies.',
    
    // Reviews Section
    reviews: [
      {
        author: 'James W.',
        business: 'Digital Marketing Agency',
        location: 'London',
        rating: 5,
        text: 'The automated discovery questionnaire changed our sales process completely. We pre-qualify leads by budget, goals, and timeline before spending an hour on a call. Only talk to businesses who can afford £2K+ monthly retainers now. Our close rate went from 22% to 68% in 5 months. Time saved is unreal.',
        verified: true
      },
      {
        author: 'Sarah T.',
        business: 'Performance Marketing Consultancy',
        location: 'Manchester',
        rating: 5,
        text: 'The ROI calculator is a game-changer. Prospects input their average order value and conversion rates, and it shows them exactly what results they can expect from our services. Makes the value crystal clear before we even send a proposal. Closed £147K in new retainer business in the first 6 months using this system.',
        verified: true
      },
      {
        author: 'David L.',
        business: 'B2B Marketing Agency',
        location: 'Bristol',
        rating: 5,
        text: 'The proposal automation generates professional marketing proposals in 8 minutes instead of 3 hours. Includes relevant case studies, custom pricing packages, and projected ROI automatically. The follow-up sequences also recover about 35% of lost proposals. We now respond to RFPs same-day and win way more competitive pitches.',
        verified: true
      },
      {
        author: 'Emma K.',
        business: 'Growth Marketing Studio',
        location: 'Edinburgh',
        rating: 5,
        text: 'The retainer renewal automation is brilliant. We get 60-day and 30-day alerts before client contracts end with automated performance summaries and upsell suggestions. Our retainer renewal rate went from 71% to 94%. The client success portal with monthly dashboards also makes clients see exactly why they\'re paying us. Worth every penny.',
        verified: true
      }
    ]
  };

  return (
    <>
      <SEO 
        title="CRM for Marketing Agency UK | Marketing Automation | Local Business Heroes"
        description="Rated 4.9/5. CRM and marketing automation for marketing agency businesses. Capture more leads, automate follow-up, grow faster. Start £5 trial today."
        keywords="CRM for marketing agency UK, marketing agency business software, marketing agency customer management, marketing agency lead generation UK"
        url="/industries/marketing-agency"
        schemaMarkup={[faqSchema, localBusinessSchema]}
        breadcrumbs={breadcrumbs}
      />
      <IndustryPageTemplate industry={industryData} />
    </>
  );
};

export default MarketingAgency;